import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { CompanyData, EndUser } from '@driftt/api-types'

import { findAccount } from 'api/AccountAPI'
import { fetchSalesforceRefreshFieldsById } from 'api/ConversationAPI'
import { fetchEndUserData, fetchEndUserTimeline } from 'api/CustomerAPI'
import { getUnifiedContact } from 'api/UnifiedContactsAPI'
import { Metadata } from 'components/Metadata'
import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { SalesforceContext } from 'context/salesforceContext'
import useDriftContactProperties from 'hooks/useDriftContactProperties'
import { AccountDetailsPageParams } from 'pages/CCRMAccountDetailsPage'
import { Timeline } from 'typings/timeline'
import { segmentTrack, TrackType } from 'utils/tracking'
import { getURLSearchParam } from 'utils/urlUtils'

import AttributesTab from './AttributesTab'
import ContactDetailsHeader, { PersonalIntel } from './ContactDetailsHeader'
import FeedTab from './FeedTab'

import styles from './styles.module.css'

const TABS = ['Activity', 'Attributes']
const TAB_SEARCH_PARAM_KEY = 'tab'
const DEFAULT_TAB = 'Activity'

interface AccountAndEndUserData {
  accountData: any
  endUserData: EndUser
  unifiedAttributes: any
  companyIntel: CompanyData
  personalIntel: PersonalIntel
}

const ContactDetailsPage: React.FC = () => {
  const { id: endUserId } = useParams<AccountDetailsPageParams>()
  const tab = getURLSearchParam(TAB_SEARCH_PARAM_KEY) || DEFAULT_TAB
  const attributeDefs = useContext(SalesforceContext)
  const contactProperties = useDriftContactProperties(endUserId)

  const [activeTab, setActiveTab] = useState<string>(tab)

  const LoadingComponent = driftLoadingStateBuilder('Loading contact profile...')
  const ErrorComponent = driftErrorStateBuilder(
    'this contact profile',
    'Try going back to a previous page'
  )

  const { data: accountAndEndUserData, error: endUserError, isLoading } = useQuery<
    AccountAndEndUserData,
    AxiosError
  >({
    queryKey: ['fetchEndUserData', endUserId],
    queryFn: async () => {
      const endUserData = await fetchEndUserData(endUserId).then(res => res.data)
      const unifiedContact = await getUnifiedContact(endUserId)

      const orgId = endUserData?.orgId
      const domain = unifiedContact?.mostRecentDriftIntelData?.companyData?.domain

      let accountData = undefined
      if (orgId && domain) {
        accountData = await findAccount(orgId, domain)
      }
      return {
        accountData,
        endUserData,
        unifiedAttributes: unifiedContact?.mergedAttributes,
        companyIntel: unifiedContact?.mostRecentDriftIntelData?.companyData,
        personalIntel: unifiedContact?.mostRecentDriftIntelData?.personalData
      }
    }
  })

  const { data: mostRecentChat } = useQuery<Timeline>({
    queryKey: ['fetchEndUserTimeline', endUserId],
    queryFn: () =>
      fetchEndUserTimeline(endUserId, { size: 1 }).then(data =>
        data.length < 1 ? undefined : data[0]
      )
  })

  const { data: salesforceData, isLoading: isSalesforceLoading } = useQuery({
    queryKey: ['fetchSalesforceRefreshFieldsById', endUserId, attributeDefs],
    queryFn: () => fetchSalesforceRefreshFieldsById(endUserId, attributeDefs)
  })

  useEffect(() => {
    if (accountAndEndUserData?.accountData && accountAndEndUserData?.companyIntel) {
      segmentTrack(TrackType.CONTACT_DETAILS_PAGE_ACCESSED, {
        accoundId: accountAndEndUserData?.accountData.id,
        companyName: accountAndEndUserData?.companyIntel.name
      })
    }
  }, [accountAndEndUserData])

  if (isLoading) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.loader}>
          <LoadingComponent />
        </div>
      </div>
    )
  }

  if (endUserError) {
    return (
      <div className={styles.emptyPage}>
        <ErrorComponent />
      </div>
    )
  }

  return (
    <div id="contactDetailsPage" className={styles.contactDetailsPage}>
      <Metadata
        title={`Drift | ${accountAndEndUserData?.endUserData?.name ?? 'Contact details'}`}
      />
      {accountAndEndUserData?.endUserData && (
        <ContactDetailsHeader
          tabNames={TABS}
          activeTab={activeTab}
          contact={accountAndEndUserData?.endUserData}
          onTabSelected={setActiveTab}
          unifiedAttributes={accountAndEndUserData?.unifiedAttributes}
          personalIntel={accountAndEndUserData?.personalIntel}
          companyIntel={accountAndEndUserData?.companyIntel}
          salesforceData={salesforceData}
          accountId={accountAndEndUserData?.accountData?.accountId}
          contactProperties={contactProperties}
        />
      )}
      <div className={styles.body}>
        {activeTab === 'Activity' && <FeedTab mostRecentChat={mostRecentChat} />}
        {accountAndEndUserData?.endUserData && activeTab === 'Attributes' && (
          <AttributesTab
            user={accountAndEndUserData?.endUserData}
            contactProperties={contactProperties}
            salesforceData={salesforceData}
            isSalesforceLoading={isSalesforceLoading}
          />
        )}
      </div>
    </div>
  )
}

export default ContactDetailsPage
